import React, { useState } from 'react';
import { Badge, Button, List, Popover, Typography } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
    useGetMyCrmRemindersQuery,
    useUpdateCrmTaskMutation,
} from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';
import { formatAest } from './crmUi';

const { Text } = Typography;

// Header bell (SYR-95): the caller's CRM reminders that are due or overdue.
// Lives in the admin header, polls lightly, and deep-links into the CRM with
// the account drawer already open (?company=).
export const CrmReminderBell: React.FC = () => {
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const { data: reminders } = useGetMyCrmRemindersQuery(
        { jwtToken },
        { skip: !jwtToken, pollingInterval: 60000 },
    );
    const [updateTask] = useUpdateCrmTaskMutation();

    const count = reminders?.length || 0;

    const content = (
        <div style={{ width: 340 }}>
            <List
                size="small"
                dataSource={reminders || []}
                locale={{
                    emptyText: (
                        <Text type="secondary">
                            <FormattedMessage
                                id="crm.reminders.empty"
                                defaultMessage="No reminders due"
                            />
                        </Text>
                    ),
                }}
                renderItem={(reminder) => (
                    <List.Item
                        style={{ cursor: 'pointer', paddingLeft: 8, paddingRight: 8 }}
                        onClick={() => {
                            setOpen(false);
                            navigate(`/admin/crm?company=${reminder.crmCompanyId}`);
                        }}
                        actions={[
                            <Button
                                key="done"
                                size="small"
                                icon={<CheckOutlined style={{ marginRight: 4 }} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    updateTask({
                                        jwtToken,
                                        id: reminder.id,
                                        payload: { status: 'done' },
                                    });
                                }}
                            >
                                <FormattedMessage id="crm.task.markDone" defaultMessage="Done" />
                            </Button>,
                        ]}
                    >
                        <List.Item.Meta
                            title={<Text ellipsis>{reminder.title}</Text>}
                            description={
                                <>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                        {reminder.companyName}
                                    </Text>
                                    <Text type="danger" style={{ fontSize: 12 }}>
                                        <FormattedMessage
                                            id="crm.reminders.dueAt"
                                            defaultMessage="Due {when} AEST"
                                            values={{ when: formatAest(reminder.dueAt) }}
                                        />
                                    </Text>
                                </>
                            }
                        />
                    </List.Item>
                )}
            />
        </div>
    );

    return (
        <Popover
            content={content}
            title={
                <FormattedMessage id="crm.reminders.title" defaultMessage="CRM reminders" />
            }
            trigger="click"
            open={open}
            onOpenChange={setOpen}
            placement="bottomRight"
        >
            <Badge count={count} size="small">
                <Button type="text" icon={<BellOutlined />} size="large" />
            </Badge>
        </Popover>
    );
};
